import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMatch } from "react-router-dom";

import Alert from "components/atoms/alert/Alert";

import {
    deleteReference,
    getReference,
    getReferences,
    patchReference,
    postReference,
    postBulkReference,
    putBibJson,
    addFolderReference,
    addTagReference,
    changeFolderOrder,
    changeTagOrder,
    deleteBulkFolder,
    deleteBulkTag,
    deleteTag,
    getFolders,
    getTagRecommendation,
    getTags,
    mergeFolder,
    postFolder,
    postTag,
    putFolder,
    putTag,
} from "utils/request/reference";

const useCurrentSpaceUuid = () => {
    const match = useMatch("/:space_uuid/*");
    return match?.params?.space_uuid ?? "";
};

export const useReferenceList = (params, options = {}) => {
    const space_uuid = useCurrentSpaceUuid();
    const { enabled: paramsEnabled, ...requestParams } = params || {};
    const enabled = options.enabled ?? paramsEnabled ?? true;

    return useQuery(["referenceList", space_uuid, requestParams], () => getReferences(space_uuid, requestParams), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        keepPreviousData: true,
        ...options,
        enabled,
    });
};

export const useReference = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["reference", space_uuid, params?.reference_no], () => getReference(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        enabled: !!params?.reference_no,
    });
};

export const useReferenceCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((variables) => postReference(space_uuid, variables), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "volume" });
        },
        onError: (error, variables, context) => {
            if (error.response.status === 400) {
                if (error.response.data.bib_json) {
                    if (error.response.data.bib_json.citation_key) {
                        Alert("warn", "에러", error.response.data.bib_json.citation_key);
                    } else {
                        Alert("warn", "에러", error.response.data.bib_json);
                    }
                } else if (error.response.data.maximum_references) {
                    Alert("warn", "에러", error.response.data.maximum_references);
                } else {
                    Alert("warn", "에러", "참고문헌 추가에 실패했습니다.");
                }
            } else {
                Alert("warn", "에러", "서버에 오류가 발생했습니다.");
            }
        },
    });
};

export const useReferenceBulkCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => postBulkReference(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "volume" });
        },
        onError: (error, variables, context) => {
            if (error.response.status !== 400) {
                Alert("warn", "에러", "서버에 오류가 발생했습니다.");
            }
        },
    });
};

export const useReferenceUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((body) => patchReference(space_uuid, body), {
        mutationKey: "referenceUpdate",
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
            queryClient.invalidateQueries({ queryKey: "bookmarkedReferences" });
            queryClient.invalidateQueries({ queryKey: "volume" });
        },
        onError: (error, variables, context) => {
            if (error.response.status === 400) {
                Alert("warn", "에러", error.response?.data?.bib_json || "참고문헌 수정에 실패했습니다.");
            } else if (error.response.status === 500) {
                console.log(error.response);
                Alert("warn", "에러", "서버에 오류가 발생했습니다.");
            }
        },
    });
};

export const useBibJsonUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((body) => putBibJson(space_uuid, body), {
        mutationKey: "bibJsonUpdate",
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
        },
    });
};

export const useReferenceDelete = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((body) => deleteReference(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "research" });
            queryClient.invalidateQueries({ queryKey: "tags" });
            queryClient.invalidateQueries({ queryKey: "bookmarkedReferences" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "volume" });
        },
    });
};

export const useTags = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["tags", space_uuid], (e) => getTags(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useTagCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => postTag(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "tags" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
        },
    });
};

export const useTagUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => putTag(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "tags" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
        },
    });
};

export const useTagDelete = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => deleteTag(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "tags" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
        },
    });
};

export const useTagBulkDelete = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => deleteBulkTag(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "tags" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
        },
    });
};

export const useTagReferenceCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => addTagReference(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "tags" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
        },
    });
};

export const useTagRecommendation = () => {
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((body) => getTagRecommendation(space_uuid, body), {
        mutationKey: "tagRecommendation",
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useTagChangeOrder = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => changeTagOrder(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "tags" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
        },
    });
};

export const useFolders = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["folders", space_uuid], (e) => getFolders(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};

export const useFolderCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => postFolder(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "folders" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
        },
    });
};

export const useFolderUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => putFolder(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "folders" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
        },
    });
};

export const useFolderDelete = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => deleteBulkFolder(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "folders" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
        },
    });
};

export const useFolderReferenceCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => addFolderReference(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "folders" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
        },
    });
};

export const useFolderChangeOrder = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => changeFolderOrder(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "folders" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
        },
    });
};

export const useFolderMerge = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => mergeFolder(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "folders" });
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
        },
    });
};
